/*
 * CAPTURE-OWNED MODULE
 * OCR Word Overlay Component for capture-ui
 * Moved from root /src to enforce repo boundaries
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { captureAPI } from '../services/api';
import { buildSelectionPayload } from '../utils/anchorInference';
import './OcrWordOverlay.css';

const OcrWordOverlay = ({ 
  documentId, 
  currentPageNumber, 
  pageSurfaceRef, 
  // No engine default. Omitting the filter returns words from EVERY acquisition
  // route, which is the only value that stays correct when a route is added.
  //
  // This was `'merged'`, and 'merged' is not a stored value — it is a synthetic
  // query mode meaning "paddle + tesseract", so it STRUCTURALLY excluded
  // anything text-layer-derived. Widening what 'merged' means was the real fix;
  // the server now treats it as "every engine, deduplicated", and the honest
  // default here is simply not to filter at all.
  engine = undefined,
  showOverlay = true,
  onWordHover,
  onWordClick,
  clickToIndex = false,
  // When true, a single-word selection is fully handled by the mousedown→mouseup
  // path (which enriches the payload with the inferred anchor). The bare onClick
  // fallback is suppressed so a one-word click fires onWordClick exactly once,
  // with the anchor-bearing payload — never a second, null-anchor call.
  // Default false preserves the plain-click behavior other consumers rely on
  // (e.g. TemplateEditorPage identifierPickMode, which reads word.text).
  enrichSingleClick = false,
  // When true, a selection of ONE token that prints a label and its value
  // together ("Phone: (800) 800-4239") is split: the emitted text is the value,
  // the label becomes the anchor, and the position is 'inline_after_anchor'.
  // That is what a Review correction wants. Default false, because this
  // handler is shared: TemplateEditorPage's identifierPickMode reads word.text
  // as the identifier itself, and "Vendor: Acme" must stay "Vendor: Acme".
  splitInlineLabels = false,
  anchorWordIds = null,
  valueWordIds = null,
  onWordsLoaded = null
}) => {
  const [ocrWords, setOcrWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlayDimensions, setOverlayDimensions] = useState({ width: 0, height: 0 });
  const resizeObserverRef = useRef(null);
  const overlayRef = useRef(null);

  // Drag-to-select state for click-to-index
  const [isDragging, setIsDragging] = useState(false);
  const [selectedWordIds, setSelectedWordIds] = useState(new Set());
  const dragStartRef = useRef(null);

  // Convert 1-based currentPageNumber to 0-based pageIndex
  const pageIndex = currentPageNumber - 1;

  // Fetch OCR words when document or page changes (with cancellation for strict mode)
  useEffect(() => {
    let cancelled = false;
    const doFetch = async () => {
      if (!documentId || pageIndex < 0) {
        setOcrWords([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        console.log(`[OcrWordOverlay] Fetching OCR words: documentId=${documentId}, pageIndex=${pageIndex}, engine=${engine}`);
        const response = await captureAPI.getOcrWords({ documentId, pageIndex, engine });
        if (!cancelled && response.ok) {
          setOcrWords(response.words || []);
          if (onWordsLoaded) onWordsLoaded(response.words || []);
          console.log(`[OcrWordOverlay] Loaded ${response.words?.length || 0} words for page ${pageIndex}`);
        } else if (!cancelled) {
          throw new Error('Failed to fetch OCR words');
        }
      } catch (err) {
        if (!cancelled) {
          console.error(`[OcrWordOverlay] Error fetching OCR words:`, err);
          setError(err.message);
          setOcrWords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [documentId, pageIndex, engine]);

  // Set up ResizeObserver to track overlay container size changes.
  // Measures the canvas element directly because it is the exact pixel surface
  // PDF.js renders into — its dimensions match the OCR normalized coordinate space.
  // If the canvas is not yet present at effect setup time (PDF still loading),
  // a MutationObserver watches for it to appear and then re-attaches.
  useEffect(() => {
    if (!overlayRef.current) return;

    const getCanvas = () => pageSurfaceRef?.current?.querySelector('canvas');

    const updateDimensions = () => {
      const canvas = getCanvas();
      const target = canvas || pageSurfaceRef?.current || overlayRef.current;
      if (target) {
        const rect = target.getBoundingClientRect();
        setOverlayDimensions({
          width: rect.width,
          height: rect.height
        });
      }
    };

    const attachResizeObserver = () => {
      const canvas = getCanvas();
      const measureTarget = canvas || pageSurfaceRef?.current || overlayRef.current;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (window.ResizeObserver && measureTarget) {
        resizeObserverRef.current = new ResizeObserver(() => {
          updateDimensions();
        });
        resizeObserverRef.current.observe(measureTarget);
      }
    };

    // Initial measurement + observer on canvas (or container if canvas not yet rendered)
    updateDimensions();
    attachResizeObserver();

    // If canvas is not yet rendered, watch for it to appear and re-measure
    let mutationObserver = null;
    if (!getCanvas() && pageSurfaceRef?.current) {
      mutationObserver = new MutationObserver(() => {
        if (getCanvas()) {
          mutationObserver.disconnect();
          mutationObserver = null;
          updateDimensions();
          attachResizeObserver();
        }
      });
      mutationObserver.observe(pageSurfaceRef.current, { childList: true, subtree: true });
    }

    // Fallback: listen for window resize
    const handleWindowResize = () => {
      setTimeout(updateDimensions, 100);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [pageSurfaceRef, overlayRef]);

  // Memoize word boxes to prevent unnecessary re-renders
  const wordBoxes = useMemo(() => {
    if (!showOverlay || !ocrWords.length || !overlayDimensions.width || !overlayDimensions.height) {
      return [];
    }

    return ocrWords.map((word) => {
      // Convert normalized coordinates (0..1) to pixel coordinates
      const left = word.bbox.x1 * overlayDimensions.width;
      const top = word.bbox.y1 * overlayDimensions.height;
      const width = (word.bbox.x2 - word.bbox.x1) * overlayDimensions.width;
      const height = (word.bbox.y2 - word.bbox.y1) * overlayDimensions.height;

      return {
        id: word.id,
        text: word.text,
        source: word.source || 'paddle',
        bbox: word.bbox, // preserved so onWordClick handlers (e.g. field zone placement) can use coordinates
        style: {
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
          border: '1px solid rgba(0, 150, 255, 0.6)',
          backgroundColor: 'rgba(0, 150, 255, 0.1)',
          pointerEvents: onWordClick || onWordHover || clickToIndex ? 'auto' : 'none',
          cursor: clickToIndex ? 'default' : (onWordClick ? 'pointer' : 'default'),
          fontSize: '0px', // Hide text by default
          overflow: 'hidden',
          boxSizing: 'border-box'
        }
      };
    });
  }, [ocrWords, overlayDimensions, showOverlay, onWordClick, onWordHover, clickToIndex]);

  // Handle word interactions
  const handleWordMouseEnter = useCallback((word) => {
    if (onWordHover) {
      onWordHover(word, 'enter');
    }
  }, [onWordHover]);

  const handleWordMouseLeave = useCallback((word) => {
    if (onWordHover) {
      onWordHover(word, 'leave');
    }
  }, [onWordHover]);

  const handleWordClick = useCallback((word, event) => {
    if (onWordClick) {
      event.stopPropagation();
      onWordClick(word);
    }
  }, [onWordClick]);

  // ── Drag-to-select handlers (click-to-index mode) ──

  const handleWordMouseDown = useCallback((wordBox, event) => {
    if (!clickToIndex) return;
    event.preventDefault(); // Prevent text selection
    setIsDragging(true);
    setSelectedWordIds(new Set([wordBox.id]));
    dragStartRef.current = wordBox.id;
  }, [clickToIndex]);

  const handleWordMouseEnterDrag = useCallback((wordBox) => {
    if (!isDragging || !clickToIndex) return;
    setSelectedWordIds(prev => {
      const next = new Set(prev);
      next.add(wordBox.id);
      return next;
    });
  }, [isDragging, clickToIndex]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging || !clickToIndex) return;
    setIsDragging(false);

    if (selectedWordIds.size === 0) return;

    // The payload (text, union bbox, inferred anchor, and the opt-in inline
    // split) is built by a pure helper so it can be tested without rendering
    // this component. Existing single-purpose consumers (e.g.
    // TemplateEditorPage's identifierPickMode) only use `text` and ignore the
    // extra fields.
    const payload = buildSelectionPayload(ocrWords, selectedWordIds, { splitInlineLabels });
    if (payload && onWordClick) onWordClick(payload);

    setSelectedWordIds(new Set());
    dragStartRef.current = null;
  }, [isDragging, clickToIndex, selectedWordIds, ocrWords, onWordClick, splitInlineLabels]);

  // Global mouseup listener to catch mouseup outside the overlay
  useEffect(() => {
    if (!isDragging) return;
    const onGlobalMouseUp = () => handleMouseUp();
    window.addEventListener('mouseup', onGlobalMouseUp);
    return () => window.removeEventListener('mouseup', onGlobalMouseUp);
  }, [isDragging, handleMouseUp]);

  if (!showOverlay) {
    return null;
  }

  return (
    <div 
      ref={overlayRef}
      className={`ocr-word-overlay${clickToIndex ? ' click-to-index' : ''}${isDragging ? ' is-dragging' : ''}`}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 10 // Above PDF canvas, below zone overlays
      }}
    >
      {loading && (
        <div className="ocr-overlay-status loading">
          <span>Loading OCR words...</span>
        </div>
      )}
      
      {error && (
        <div className="ocr-overlay-status error">
          <span>Error loading OCR words: {error}</span>
        </div>
      )}

      {!loading && !error && ocrWords.length === 0 && (
        <div className="ocr-overlay-status empty">
          <span>No OCR words found for this page</span>
        </div>
      )}

      {/* Render word bounding boxes */}
      {wordBoxes.map((wordBox) => (
        <div
          key={wordBox.id}
          className={[
            'ocr-word-box',
            wordBox.source === 'tesseract' ? 'ocr-word-fillin' : '',
            selectedWordIds.has(wordBox.id) ? 'ocr-word-selected' : '',
            anchorWordIds && anchorWordIds.has(wordBox.id) ? 'ocr-word-anchor' : '',
            valueWordIds && valueWordIds.has(wordBox.id) ? 'ocr-word-value' : '',
          ].filter(Boolean).join(' ')}
          style={wordBox.style}
          title={`OCR: "${wordBox.text}"`}
          onMouseEnter={() => {
            handleWordMouseEnter(wordBox);
            handleWordMouseEnterDrag(wordBox);
          }}
          onMouseLeave={() => handleWordMouseLeave(wordBox)}
          onClick={(e) => {
            // In enrich-single-click mode the mousedown→mouseup path already
            // emitted the enriched (anchor-bearing) payload for this word, so
            // this bare click would be a redundant, null-anchor double-fire.
            if (enrichSingleClick) return;
            if (!isDragging) handleWordClick(wordBox, e);
          }}
          onMouseDown={(e) => handleWordMouseDown(wordBox, e)}
          onMouseUp={() => handleMouseUp()}
        >
          <span className="ocr-word-text">{wordBox.text}</span>
        </div>
      ))}

      {/* Debug info (only in development) */}
      {import.meta.env?.DEV && (
        <div className="ocr-overlay-debug">
          <small>
            Page: {currentPageNumber} | Words: {ocrWords.length} | 
            Size: {overlayDimensions.width}×{overlayDimensions.height}
          </small>
        </div>
      )}
    </div>
  );
};

export default OcrWordOverlay;
